import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AUTH_EXPIRED_BROADCAST_KEY,
  AUTH_EXPIRED_EVENT,
  isActivePracticeRoute,
  type AuthExpiredReason,
} from '@/features/shared/api/client';
import { useStudentAuthStore } from '@/features/auth/store/authStore';

type AuthExpiredDialogState = {
  title: string;
  message: string;
};

function resolveDialogState(reason: AuthExpiredReason, fallbackMessage?: string): AuthExpiredDialogState {
  if (reason === 'device_replaced') {
    return {
      title: '当前设备已下线',
      message: fallbackMessage || '你的账号已在另一台设备登录，当前设备已被自动下线，请重新登录后继续练习。',
    };
  }
  if (reason === 'account_blocked') {
    return {
      title: '账号已被限制',
      message: fallbackMessage || '该账号因设备数超限或账号状态异常暂不可继续使用，请联系管理员处理。',
    };
  }
  if (reason === 'logged_out') {
    return {
      title: '你已退出登录',
      message: fallbackMessage || '当前账号已退出登录，如需继续练习，请重新登录。',
    };
  }
  if (reason === 'token_expired') {
    return {
      title: '登录已超时',
      message: fallbackMessage || '本次登录已超时失效，请重新登录后继续练习。',
    };
  }
  if (reason === 'token_invalid' || reason === 'session_missing' || reason === 'session_not_found' || reason === 'session_revoked') {
    return {
      title: '登录状态已失效',
      message: fallbackMessage || '当前登录状态已失效，请重新登录后继续练习。',
    };
  }
  if (reason === 'account_unavailable') {
    return {
      title: '账号暂不可用',
      message: fallbackMessage || '当前账号状态异常，暂时无法继续使用，请重新登录或联系管理员。',
    };
  }
  return {
    title: '登录状态已失效',
    message: fallbackMessage || '登录态已失效，请重新登录。',
  };
}

export function AuthExpiredDialog() {
  const location = useLocation();
  const navigate = useNavigate();
  const clearSession = useStudentAuthStore((state) => state.clearSession);
  const [visible, setVisible] = useState(false);
  const [dialogState, setDialogState] = useState<AuthExpiredDialogState>({
    title: '登录状态已失效',
    message: '登录态已失效，请重新登录。',
  });

  useEffect(() => {
    const applyExpiredState = (detail?: { message?: string; reason?: AuthExpiredReason }) => {
      if (!isActivePracticeRoute(location.pathname)) {
        clearSession();
        navigate('/login', { replace: true });
        return;
      }
      setDialogState(resolveDialogState(detail?.reason || 'unknown', detail?.message));
      setVisible(true);
    };

    const handleExpired = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string; reason?: AuthExpiredReason }>).detail;
      applyExpiredState(detail);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_EXPIRED_BROADCAST_KEY || !event.newValue) {
        return;
      }
      try {
        const detail = JSON.parse(event.newValue) as { message?: string; reason?: AuthExpiredReason };
        applyExpiredState(detail);
      } catch {
        applyExpiredState();
      }
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [clearSession, location.pathname, navigate]);

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[color:var(--color-overlay-scrim)] p-6">
      <div
        className="w-full max-w-[420px] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-lg)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-auth-expired-title"
      >
        <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-full)] bg-[var(--color-warning-light)] text-[var(--color-warning)]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 7v5" />
            <path d="M12 16h.01" />
          </svg>
        </div>
        <h2 id="student-auth-expired-title" className="mb-2 text-xl font-display font-semibold text-[var(--color-text)]">
          {dialogState.title}
        </h2>
        <p className="mb-6 text-sm leading-6 text-[var(--color-text-secondary)]">{dialogState.message}</p>
        <button
          type="button"
          onClick={() => {
            setVisible(false);
            clearSession();
            navigate('/login', { replace: true });
          }}
          className="inline-flex h-11 w-full items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-primary)] text-sm font-semibold text-[var(--color-text-inverse)] transition-colors hover:bg-[var(--color-primary-hover)]"
        >
          重新登录
        </button>
      </div>
    </div>
  );
}
